import React from "react";
import "./About.css";
import AccuracyComparison from "../components/AccuracyComparison";

const About = () => {
  return (
    <div className="about-container">
      <h1>About Byte Burrower</h1>

      <p className="intro">
        Byte Burrower is the world's first AI groundhog, bringing the tradition of Groundhog Day predictions into the digital age with data-driven insights. By analyzing weather forecasts and 20 years of historical climate data specific to your location, Byte Burrower offers playful and personalized predictions about whether spring will arrive early or if winter will linger.
      </p>

      <h2>How It Works</h2>

      <p>
        Unlike traditional groundhogs that rely on seeing their shadows, Byte Burrower uses a <strong>Bayesian-inspired approach</strong> combining historical weather patterns with current forecasts:
      </p>

      <ol className="methodology-list">
        <li>
          <strong>Historical Prior:</strong> We analyze 20 years of February and March temperature data for your location. Each year is classified as "early spring" or "long winter" based on whether temperatures exceeded the 15-year rolling average. The proportion of early spring years becomes your location's baseline probability.
        </li>
        <li>
          <strong>Forecast Signal:</strong> We fetch the forecast high temperature for Groundhog Day and compare it to historical averages. Warmer forecasts push the probability toward early spring.
        </li>
        <li>
          <strong>Bayesian Combination:</strong> The historical prior and forecast signal are blended together. Closer to Groundhog Day, the forecast has more influence (up to 60%); further out, history dominates (up to 88%).
        </li>
        <li>
          <strong>AI Message:</strong> GPT-4 generates a playful, personalized prediction based on the probability and context.
        </li>
      </ol>

      <h2>Early Spring Definition</h2>

      <p>
        Following the methodology of <a href="https://www.ncei.noaa.gov/news/groundhog-day-forecasts-and-climate-history" target="_blank" rel="noopener noreferrer">NOAA</a> and <a href="https://fivethirtyeight.com/features/groundhogs-do-not-make-good-meteorologists/" target="_blank" rel="noopener noreferrer">FiveThirtyEight</a>, a year is classified as "early spring" if either February OR March has an average high temperature above the 15-year rolling average for that month. Unlike previous analyses that used national or regional data, Byte Burrower evaluates predictions using <strong>local weather data</strong> for each location.
      </p>

      <h2>Historical Accuracy Comparison</h2>

      <p>
        For each location, Byte Burrower shows how its predictions stack up against the nearest real prognosticator. But wait—Byte Burrower was created in 2024. How can it have historical predictions?
      </p>

      <p>
        The answer: <strong>retroactive calculation</strong>. We apply the same algorithm to past weather data, calculating what Byte Burrower <em>would have</em> predicted each year based on the historical baseline at that time. This allows for a fair comparison against prognosticators with decades of predictions.
      </p>

      <AccuracyComparison />

      <h2>Frequently Asked Questions</h2>

      <div className="faq">
        <h3>Who created Byte Burrower?</h3>
        <p>
          That's a tough question. On September 13, 2024, <a href="https://globalhealth.duke.edu/people/green-eric" target="_blank" rel="noopener noreferrer">Eric Green</a>, who teaches data science at Duke University, asked ChatGPT to create the first AI groundhog while recording an episode for the <a href="https://countdowntogroundhogday.com/podcast" target="_blank" rel="noopener noreferrer">"Countdown to Groundhog Day" podcast</a>. The AI groundhog named itself Byte Burrower. Later, Eric returned to ChatGPT (and Claude) to help develop this web app. So who can say, really?
        </p>

        <h3>Where does the prognosticator data come from?</h3>
        <p>
          Prognosticator data comes from the <a href="https://github.com/ericpgreen/feb2" target="_blank" rel="noopener noreferrer">feb2 R package</a>, which sources its data from <a href="https://countdowntogroundhogday.com/" target="_blank" rel="noopener noreferrer">Countdown to Groundhog Day</a>—the internet's most comprehensive database of Groundhog Day prognosticators. The 2-sentence descriptions are AI-generated summaries of each prognosticator's biography.
        </p>

        <h3>Where does the weather data come from?</h3>
        <p>
          Historical weather data and forecasts come from <a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer">Open-Meteo</a>, an open-source weather API. Geocoding (converting city names to coordinates) uses OpenWeatherMap.
        </p>

        <h3>Is Byte Burrower more accurate than real prognosticators?</h3>
        <p>
          It depends on the location! Check the historical accuracy comparison for your nearest prognosticator to see how they stack up. Spoiler: most prognosticators (including Punxsutawney Phil) perform close to random chance when evaluated with local weather data.
        </p>

        <h3>How can I contact you?</h3>
        <p>
          Have questions or feedback? Reach out at <a href="mailto:epgreen+byteburrower@gmail.com">epgreen+byteburrower@gmail.com</a>.
        </p>
      </div>

      <h2>Related Projects</h2>

      <ul className="related-links">
        <li>
          <a href="https://github.com/ericpgreen/feb2" target="_blank" rel="noopener noreferrer">feb2 R package</a> — Groundhog Day prediction data and accuracy metrics
        </li>
        <li>
          <a href="https://countdowntogroundhogday.com/" target="_blank" rel="noopener noreferrer">Countdown to Groundhog Day</a> — Comprehensive prognosticator database
        </li>
        <li>
          <a href="https://groundhogday.site/episode/Data-Science-and-Byte-Burrower" target="_blank" rel="noopener noreferrer">Data Science and Byte Burrower</a> — Podcast episode on the data science behind Groundhog Day
        </li>
      </ul>
    </div>
  );
};

export default About;
